import { cx } from "@emotion/css";
import CircularProgress from "@mui/material/CircularProgress";
import { Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import routing from "routing";
import { OutlinedPrimaryButton } from "src/components/Default/LekUI/Button";
import type { SortOption } from "src/components/Default/LekUI/SortButton";
import { SortButton } from "src/components/Default/LekUI/SortButton";
import { ViewBox } from "src/components/Default/Main/ViewBox";
import { FolderEmptyListView } from "src/components/Folders/FolderEmptyListView";
import { FolderSelectionCheckbox } from "src/components/Folders/FolderSelectionCheckbox";
import { ConfirmationModal } from "src/components/Modals/ConfirmationModals/ConfirmationModal";
import { UserDeckCard } from "src/components/NewComponents/Flashcards/UserDecks/UserDeckCard";
import { DecksSelectionBar } from "src/components/SelectionBar/DecksSelectionBar";
import { useDebounce } from "src/hooks/useDebounce";
import type {
	DeckSortBy,
	DeckSortOrder,
	SearchDeck,
} from "src/hooks/useFlashcards";
import { useRemoveDecks, useSearchUserDecks } from "src/hooks/useFlashcards";
import { useSelectionMode } from "src/hooks/useSelectionMode";
import FishIcon from "src/images/Icons/newIcons/searchTypes/fish.svg";

const SORT_OPTIONS: SortOption<DeckSortBy, DeckSortOrder>[] = [
	{
		orderBy: "createdAt",
		order: "desc",
		labelKey: "decks.sort.createdAt",
		labelFallback: "Data utworzenia",
	},
	{
		orderBy: "createdAt",
		order: "asc",
		labelKey: "decks.sort.createdAt",
		labelFallback: "Data utworzenia",
	},
	{
		orderBy: "name",
		order: "asc",
		labelKey: "decks.sort.alphabetical",
		labelFallback: "Alfabetycznie",
	},
	{
		orderBy: "name",
		order: "desc",
		labelKey: "decks.sort.alphabetical",
		labelFallback: "Alfabetycznie",
	},
	{
		orderBy: "flashcardsToResolveCount",
		order: "desc",
		labelKey: "decks.sort.flashcardsToResolveCount",
		labelFallback: "Liczba fiszek do rozwiązania",
	},
	{
		orderBy: "flashcardsToResolveCount",
		order: "asc",
		labelKey: "decks.sort.flashcardsToResolveCount",
		labelFallback: "Liczba fiszek do rozwiązania",
	},
];

export function UserDecksList() {
	const { t } = useTranslation();
	const history = useHistory();
	const [searchValue, setSearchValue] = useState("");
	const [selectedSort, setSelectedSort] = useState(0);
	const debouncedSearch = useDebounce(searchValue, 500);

	const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

	const getDeckKey = useCallback((code: string) => code, []);
	const {
		isSelectMode,
		selectedItems: selectedCodes,
		selectedCount,
		toggleSelection,
		isSelected: isCodeSelected,
		exitSelectMode,
		enterSelectMode,
	} = useSelectionMode<string>({ getKey: getDeckKey });

	const { mutateAsync: removeDecks, isPending: isRemoving } = useRemoveDecks();

	const currentSort = SORT_OPTIONS[selectedSort];

	const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useSearchUserDecks({
			search: debouncedSearch || undefined,
			orderBy: currentSort.orderBy,
			order: currentSort.order?.toUpperCase(),
		});

	const allDecks = useMemo(() => {
		return data?.pages.flatMap((page) => page.items) ?? [];
	}, [data]);

	const totalCount = data?.pages[0]?.totalCount ?? 0;

	const handleRemove = () => {
		if (selectedCount === 0) return;
		setShowRemoveConfirm(true);
	};

	const confirmRemove = async () => {
		await removeDecks(selectedCodes);
		setShowRemoveConfirm(false);
		exitSelectMode();
	};

	const tabs = [t("userDecks.tabs.decks", "Twoje zestawy")];

	const menuItems = [
		{
			title: t("userDecks.deleteDecks", "Usuń wiele"),
			onClick: enterSelectMode,
			Icon: <Trash2 className="icon-small icon-error" />,
			itemTextClassName: "text-error",
			testId: "user-decks-menu-delete",
		},
	];

	return (
		<>
			<div className="creator-container">
				<ViewBox
					title={t("userDecks.title", "Fiszki")}
					icon={<FishIcon className="icon-super-big icon-purple" />}
					withBackButton
					tabs={tabs}
					selectedTab={0}
					onTabChange={() => {}}
					menuItems={menuItems}
					searchProps={{
						placeholder: t("userDecks.search.placeholder", "Szukaj zestawu"),
						value: searchValue,
						onChange: setSearchValue,
						onClear: () => setSearchValue(""),
					}}
					sortButton={
						<div className="flex items-center gap-8">
							<span className="size-14 text-grey">
								{t("decks.sort.label", "Sortowanie")}
							</span>
							<SortButton
								options={SORT_OPTIONS}
								selectedIndex={selectedSort}
								onSelect={setSelectedSort}
								testId="user-decks-sort-button"
								containerName="creator-container"
							/>
						</div>
					}
				>
					{isSelectMode && (
						<DecksSelectionBar
							selectedCount={selectedCount}
							onRemove={handleRemove}
							onClose={exitSelectMode}
							isRemoving={isRemoving}
						/>
					)}
					{isLoading ? (
						<div className="flex-center-row w-100 p-64">
							<CircularProgress />
						</div>
					) : !allDecks.length ? (
						debouncedSearch ? (
							<div className="flex-center-row w-100 p-64 text-grey size-16">
								{t("userDecks.empty", "Nie masz jeszcze żadnych zestawów")}
							</div>
						) : (
							<FolderEmptyListView
								onClick={() =>
									history.push(routing.paths.pickFlashcardsDeckChoice)
								}
								text={t(
									"userDecks.emptyDescription",
									"Dodaj swój pierwszy zestaw, a wtedy pojawią się w tym miejscu",
								)}
								buttonText={t("userDecks.emptyButton", "Dodaj zestaw")}
							/>
						)
					) : (
						<div className="flex flex-col gap-16">
							<div className="divider divider--horizontal" />
							{debouncedSearch && (
								<span className="size-18">
									{t("folder.searchResultsFor", 'Wyniki dla "{{query}}"', {
										query: debouncedSearch,
									})}
								</span>
							)}
							<div className="deck-search-grid">
								{allDecks.map((deck: SearchDeck) => {
									const selected = isCodeSelected(deck.code);
									return (
										<div
											key={deck.code}
											onClick={
												isSelectMode
													? () => toggleSelection(deck.code)
													: undefined
											}
											className={cx(
												isSelectMode && "cursor-pointer p-relative",
											)}
										>
											{isSelectMode && (
												<FolderSelectionCheckbox isSelected={selected} />
											)}
											<UserDeckCard
												deck={deck}
												className={cx(
													selected && "border-color-primary",
													isSelectMode && "pointer-events-none",
												)}
											/>
										</div>
									);
								})}
							</div>
							{hasNextPage && (
								<div className="flex justify-center items-center gap-16">
									<OutlinedPrimaryButton
										onClick={() => fetchNextPage()}
										disabled={isFetchingNextPage}
									>
										{isFetchingNextPage
											? t("folder.loading", "Ładowanie...")
											: t("folder.loadMore", "Załaduj więcej")}
									</OutlinedPrimaryButton>
									<div className="flex flex-col justify-center size-14 text-black-80">
										<span>{t("folder.showing", "Wyświetlane:")}</span>
										<span>
											{t(
												"folder.showingCountValues",
												"{{loaded}} z {{total}} elementów",
												{
													loaded: allDecks.length,
													total: totalCount,
												},
											)}
										</span>
									</div>
								</div>
							)}
						</div>
					)}
				</ViewBox>
			</div>
			{showRemoveConfirm && (
				<ConfirmationModal
					onClose={() => setShowRemoveConfirm(false)}
					onConfirm={confirmRemove}
					title={t("userDecks.removeConfirmTitle", "Usuń zestawy")}
					isLoading={isRemoving}
				>
					{t(
						"userDecks.removeConfirmDescription",
						"Czy na pewno chcesz usunąć wybrane zestawy? Tej operacji nie można cofnąć.",
					)}
				</ConfirmationModal>
			)}
		</>
	);
}
